import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import HeadsetLoginQrModal from '../HeadsetLoginQrModal';

const QuratorHeader = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const profileDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-shellHeader flex h-shellHeaderQurator items-center bg-white/80 px-8 shadow-[0_8px_30px_rgba(15,23,42,0.045)] backdrop-blur-xl">
        <div className="flex items-center gap-4">
          <button
            className="rounded-lg p-2 text-2xl text-qurator-secondary transition hover:bg-slate-50"
            type="button"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle Sidebar"
          >
            <i className="bi bi-list" />
          </button>

          <Link to="/qurator-dashboard" className="inline-flex items-center">
            <img
              src="https://qure.science/images/qure.webp"
              alt="Qure"
              className="h-10 w-auto transition-transform hover:scale-105"
              style={{
                filter:
                  'invert(44%) sepia(91%) saturate(2044%) hue-rotate(347deg) brightness(99%) contrast(95%)',
              }}
            />
          </Link>

          <span className="rounded-full bg-qurator-primary px-3 py-1 text-[0.8rem] font-semibold tracking-[0.06em] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.28)]">
            Qurator
          </span>
        </div>
        
        <div className="ml-auto flex items-center gap-4">
          <button
            className="inline-flex items-center gap-2 rounded-xl bg-qurator-primary px-4 py-2 font-medium tracking-[-0.01em] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_8px_18px_rgba(217,90,23,0.22)] transition hover:-translate-y-0.5 hover:bg-qurator-primaryDark hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_12px_22px_rgba(217,90,23,0.28)]"
            onClick={() => setQrModalOpen(true)}
            type="button"
          >
            <i className="bi bi-qr-code text-xl" />
            <span className="hidden sm:inline">Headset Login</span>
          </button>

          <div className="relative z-shellDropdown" ref={profileDropdownRef}>
            <button
              className="inline-flex items-center gap-2 rounded-full px-2 py-1 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-qurator-primaryLight"
              type="button"
              onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
            >
              <img
                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                  user?.username || 'User'
                )}&background=D95A17&color=fff`}
                alt="Profile"
                className="h-10 w-10 rounded-full object-cover ring-2 ring-white shadow-[0_0_0_1px_rgba(217,90,23,0.22)]"
              />
              <span className="hidden text-sm font-medium text-slate-700 sm:inline">
                {user?.username || 'User'}
              </span>
              <i
                className={`bi bi-chevron-down text-slate-500 transition-transform ${
                  profileDropdownOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
            {profileDropdownOpen && (
              <div className="absolute right-0 top-full z-shellDropdown mt-2 min-w-[200px] overflow-hidden rounded-shellSm bg-white py-2 shadow-shellMenu">
                <Link
                  className="flex w-full items-center gap-3 px-5 py-3 text-sm text-slate-800 transition hover:bg-qurator-primaryLight hover:text-qurator-primary"
                  to="/profile"
                  onClick={() => setProfileDropdownOpen(false)}
                >
                  <i className="bi bi-person w-4 text-center" />
                  Profile
                </Link>
                {(user?.user_id === 79 || user?.user_id === '79' || parseInt(user?.user_id) === 79) && (
                  <Link
                    className="flex w-full items-center gap-3 px-5 py-3 text-sm text-slate-800 transition hover:bg-qurator-primaryLight hover:text-qurator-primary"
                    to="/admin-dashboard"
                    onClick={() => setProfileDropdownOpen(false)}
                  >
                    <i className="bi bi-shield-check w-4 text-center" />
                    Admin Dashboard
                  </Link>
                )}
                <hr className="my-2 h-px border-0 bg-slate-200" />
                <button
                  className="flex w-full items-center gap-3 px-5 py-3 text-left text-sm text-slate-800 transition hover:bg-qurator-primaryLight hover:text-qurator-primary"
                  onClick={handleLogout}
                  type="button"
                >
                  <i className="bi bi-box-arrow-right w-4 text-center" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <HeadsetLoginQrModal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        qrApiPath="/qurator/profile/qr-code"
        title="Headset Login"
        accentClass="text-qurator-secondary"
        closeHoverClass="hover:text-qurator-danger"
      />

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-shellHeaderQurator z-shellSidebar h-[calc(100vh-70px)] w-quratorSidebar overflow-y-auto border-r border-slate-200/80 bg-white/90 py-4 transition-transform lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        id="qrtSidebar"
      >
        <nav>
          <Link 
            to="/qurator-dashboard" 
            className={`flex items-center gap-4 px-6 py-3 transition ${
              isActive('/qurator-dashboard')
                ? 'border-l-4 border-qurator-primary bg-qurator-primaryLight font-semibold text-qurator-primary'
                : 'border-l-4 border-transparent text-slate-600 hover:border-qurator-primary hover:bg-qurator-primaryLight hover:text-qurator-primary'
            }`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-house w-5 text-center text-[1.1rem]" />
            Dashboard
          </Link>
          <Link 
            to="/library" 
            className={`flex items-center gap-4 px-6 py-3 transition ${
              isActive('/library')
                ? 'border-l-4 border-qurator-primary bg-qurator-primaryLight font-semibold text-qurator-primary'
                : 'border-l-4 border-transparent text-slate-600 hover:border-qurator-primary hover:bg-qurator-primaryLight hover:text-qurator-primary'
            }`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-collection w-5 text-center text-[1.1rem]" />
            Library
          </Link>
          <Link 
            to="/projects" 
            className={`flex items-center gap-4 px-6 py-3 transition ${
              isActive('/projects')
                ? 'border-l-4 border-qurator-primary bg-qurator-primaryLight font-semibold text-qurator-primary'
                : 'border-l-4 border-transparent text-slate-600 hover:border-qurator-primary hover:bg-qurator-primaryLight hover:text-qurator-primary'
            }`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-folder w-5 text-center text-[1.1rem]" />
            Projects
          </Link>
          <Link 
            to="/team-management" 
            className={`flex items-center gap-4 px-6 py-3 transition ${
              isActive('/team-management')
                ? 'border-l-4 border-qurator-primary bg-qurator-primaryLight font-semibold text-qurator-primary'
                : 'border-l-4 border-transparent text-slate-600 hover:border-qurator-primary hover:bg-qurator-primaryLight hover:text-qurator-primary'
            }`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-people w-5 text-center text-[1.1rem]" />
            Team Management
          </Link>
          <Link 
            to="/global-context" 
            className={`flex items-center gap-4 px-6 py-3 transition ${
              isActive('/global-context')
                ? 'border-l-4 border-qurator-primary bg-qurator-primaryLight font-semibold text-qurator-primary'
                : 'border-l-4 border-transparent text-slate-600 hover:border-qurator-primary hover:bg-qurator-primaryLight hover:text-qurator-primary'
            }`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-globe w-5 text-center text-[1.1rem]" />
            Global Context
          </Link>

          <div className="px-6 pb-2 pt-4 text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-slate-400">
            Workflow
          </div>
          <Link 
            to="/calendar" 
            className={`flex items-center gap-4 px-6 py-3 transition ${
              isActive('/calendar')
                ? 'border-l-4 border-qurator-primary bg-qurator-primaryLight font-semibold text-qurator-primary'
                : 'border-l-4 border-transparent text-slate-600 hover:border-qurator-primary hover:bg-qurator-primaryLight hover:text-qurator-primary'
            }`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-calendar-event w-5 text-center text-[1.1rem]" />
            Calendar
          </Link>
          <Link 
            to="/analytics" 
            className={`flex items-center gap-4 px-6 py-3 transition ${
              isActive('/analytics')
                ? 'border-l-4 border-qurator-primary bg-qurator-primaryLight font-semibold text-qurator-primary'
                : 'border-l-4 border-transparent text-slate-600 hover:border-qurator-primary hover:bg-qurator-primaryLight hover:text-qurator-primary'
            }`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-graph-up w-5 text-center text-[1.1rem]" />
            Analytics
          </Link>

          <div className="px-6 pb-2 pt-4 text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-slate-400">
            Support
          </div>
          <Link 
            to="/help-desk" 
            className={`flex items-center gap-4 px-6 py-3 transition ${
              isActive('/help-desk')
                ? 'border-l-4 border-qurator-primary bg-qurator-primaryLight font-semibold text-qurator-primary'
                : 'border-l-4 border-transparent text-slate-600 hover:border-qurator-primary hover:bg-qurator-primaryLight hover:text-qurator-primary'
            }`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-headset w-5 text-center text-[1.1rem]" />
            Help Desk
          </Link>
          <Link 
            to="/support" 
            className={`flex items-center gap-4 px-6 py-3 transition ${
              isActive('/support')
                ? 'border-l-4 border-qurator-primary bg-qurator-primaryLight font-semibold text-qurator-primary'
                : 'border-l-4 border-transparent text-slate-600 hover:border-qurator-primary hover:bg-qurator-primaryLight hover:text-qurator-primary'
            }`}
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-headset w-5 text-center text-[1.1rem]" />
            Support Ticket
          </Link>
        </nav>
      </aside>

    </>
  );
};

export default QuratorHeader;
